<script setup>
// ========================================
// Imports
// ========================================
import { useDisplay } from 'vuetify'
import SalonerLogo from '@images/logo.svg?skipsvgo' // SVGO strips the viewBox, so a resized logo gets cropped
import newSmsSoundFile from '@/assets/sounds/new-sms.wav'
import EmployeeAvatar from '@/components/EmployeeAvatar.vue'
import CommunicationNotice from '@/components/messages/CommunicationNotice.vue'
import SmsTextarea from '@/components/SmsTextarea.vue'
import SmsTextareaFooter from '@/components/SmsTextareaFooter.vue'
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { useCustomerStore } from '@/stores/CustomersStore'
import { useModalStore } from '@/stores/ModalStore'
import { useNotificationStore } from '@/stores/NotificationStore'
import { until } from '@vueuse/core'
import { extractDateFromString, extractTimeFromDateString } from '@/utils/datetime'
import { formatPhone, getInitials } from '@/utils/strings'
import { getSmsInfo, SMS_MAX_PARTS, SMS_STATUS_TONES } from '@/utils/sms'

// ========================================
// Props & Emits
// ========================================
const props = defineProps({
  // { id, name, phone, color } - name and phone are empty when the client is not on the loaded list
  client: {
    type: Object,
    required: true,
  },

  // Inside another view (customer card): no header, never full-screen on phones
  embedded: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  // data.notice of the conversation above the composer - off where the page already shows it
  showNotice: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'read', 'sent', 'newMessage', 'failed'])

// ========================================
// Composables & Stores
// ========================================
const { t, te, locale } = useI18n()
const { sm, smAndDown } = useDisplay()
const calendarStore = useCalendarStore()
const customerStore = useCustomerStore()
const modalStore = useModalStore()
const notificationStore = useNotificationStore()
const { mobile } = useDisplay()

// ========================================
// State
// ========================================
const messages = ref([])
const meta = ref({})
const isLoading = ref(true)
const isLoadingOlder = ref(false)
const isSending = ref(false)
const isPolling = ref(false)
const text = ref('')
const scrollBox = ref()
const notice = ref(null) // data.notice from BE

// ========================================
// Client & senders
// ========================================
// clientName comes with the messages (BE is adding it) - used when the client is not on the loaded list
const clientName = computed(() => props.client.name || messages.value.find(message => message.clientName)?.clientName || '')
const clientPhone = computed(() => props.client.phone || messages.value[0]?.clientPhone || '')

// sender.userId matches employeeId of calendar employees and deleted employees
const employeesById = computed(() => Object.fromEntries(
  [...calendarStore.availableCalendars, ...(calendarStore.unavailableEmployees || [])]
    .filter(employee => employee.employeeId)
    .map(employee => [String(employee.employeeId), employee]),
))

// ========================================
// Thread
// ========================================
// "2026-09-24" -> "24.09.2026"
const formatDate = date => date.split('-').reverse().join('.')

const formatDay = date => new Date(`${date}T00:00`).toLocaleDateString(locale.value, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

// Just the time when read on the day it came, otherwise with the date
const formatReadTime = (readTime, messageDate) => {
  const [date, time] = readTime.split(' ')

  return date === messageDate ? time.slice(0, 5) : `${formatDate(date).slice(0, 5)} ${time.slice(0, 5)}`
}

const threadItems = computed(() => {
  let prevDate = null
  let newDividerShown = false

  return messages.value.map(message => {
    const dateTime = message.conversationTime || message.plannedTime || ''
    const date = dateTime && extractDateFromString(dateTime)
    const senderType = message.direction === 2 ? 'client' : (message.sender?.type === 'employee' ? 'employee' : 'system')
    const isClient = senderType === 'client'

    // "New messages" goes above the first client message that was unread
    const isNew = isClient && !message.readTime && !newDividerShown
    if (isNew) newDividerShown = true

    const status = message.status
    const tone = SMS_STATUS_TONES[status?.tone]

    const item = {
      id: message.id,
      text: message.message,
      dayLabel: date && date !== prevDate ? formatDay(date) : '',
      time: dateTime && extractTimeFromDateString(dateTime),
      senderType,
      isClient,
      isNew,
      clientName: message.clientName || clientName.value,
      employee: senderType === 'employee' ? employeesById.value[String(message.sender?.userId)] : null,
      readAt: isClient && message.readTime ? formatReadTime(message.readTime, date) : '',
      parts: message.partsCount,
      // utf: true - special characters were replaced ("Ą→A" chip), false/null - no chip
      charsReplaced: message.utf === false,
      originalText: message.originalMessage,
      status: status && {
        label: te(`messages_page.status.${status.key}`) ? t(`messages_page.status.${status.key}`) : status.label,
        color: tone?.color,
        icon: tone?.icon,
      },
    }

    prevDate = date || prevDate

    return item
  })
})

const olderThan = computed(() => {
  const dateTime = messages.value[0]?.conversationTime

  return dateTime ? formatDate(extractDateFromString(dateTime)) : ''
})

/**
 * Loads the newest page of the conversation, or the page before the loaded ones when older is true.
 * @returns {Promise<boolean|null>} true on success, false on error, null when aborted (the same request was sent again)
 */
const fetchMessages = async (older = false) => {
  const params = new URLSearchParams({ id: props.client.id, perPage: 50 })
  if (older) {
    params.append('before', meta.value.nextBefore)
    params.append('beforeId', meta.value.nextBeforeId)
  }

  try {
    const response = await $backend(`/clients/getConversation?${params}`, { method: 'GET' })
    if (!response?.success) return false

    const page = response.data?.messages || []
    notice.value = response.data?.notice || null
    if (older) {
      messages.value = [...page, ...messages.value]
      meta.value = response.meta || {}
    } else {
      // Newest page (first load, after sending, polling) - keep the older messages already loaded.
      // The "load older" cursor points at the oldest loaded message, so it only changes when the page covers all of them.
      const pageIds = new Set(page.map(message => message.id))
      const loadedOlder = messages.value.filter(message => !pageIds.has(message.id))
      messages.value = [...loadedOlder, ...page]
      if (!loadedOlder.length) meta.value = response.meta || {}
    }

    return true
  } catch (e) {
    return e?.cause?.name === 'AbortError' ? null : false // failed - $backend already showed the error
  }
}

const scrollToBottom = async () => {
  await nextTick()
  if (scrollBox.value) scrollBox.value.scrollTop = scrollBox.value.scrollHeight
}

const loadOlder = async (event) => {
  // Mouse click (detail > 0) - drop the focus ring; keyboard users keep the focus
  if (event?.detail) event.currentTarget.blur()

  const box = scrollBox.value
  const distanceFromBottom = box.scrollHeight - box.scrollTop

  isLoadingOlder.value = true
  await fetchMessages(true)
  isLoadingOlder.value = false

  // Keep the messages the user was looking at in place
  await nextTick()
  box.scrollTop = box.scrollHeight - distanceFromBottom
}

// ========================================
// Polling
// ========================================
let pollTimer = null
const newSmsSound = new Audio(newSmsSoundFile)

// BE marks the conversation as read when it is fetched - the client's message notifications are read with it
const markAsRead = async () => {
  emit('read', props.client.id)

  // Notifications may still be loading (page refresh) or being read (opened from a notification)
  await until(() => notificationStore.isLoading).toBe(false)
  const notificationIds = notificationStore.clientSmsNotificationIds(props.client.id)
  if (notificationIds.length) notificationStore.readNotification(notificationIds)
}

// New messages every 30s. Stops after an error, so a failing BE doesn't show an error toast every 30s.
const poll = async () => {
  // Not in a background tab - BE marks what it returns as read
  if (document.hidden || isLoading.value || isLoadingOlder.value) return

  const box = scrollBox.value
  const wasAtBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 40
  const count = messages.value.length

  isPolling.value = true
  const loaded = await fetchMessages()
  isPolling.value = false
  if (loaded === false) return clearInterval(pollTimer)
  if (!loaded) return // aborted by a newer request for this conversation

  markAsRead()

  if (messages.value.length > count) {
    emit('newMessage', props.client.id, messages.value.at(-1)) // the conversations list shows the newest message

    // New messages are the newest ones, appended at the end
    if (messages.value.slice(count).some(message => message.direction === 2)) {
      newSmsSound.currentTime = 0
      newSmsSound.play().catch(() => {}) // autoplay blocked until the user interacts with the page
    }

    // Follow new messages only when the user isn't reading older ones
    if (wasAtBottom) scrollToBottom()
  }
}

// Parent can fetch new messages right away (e.g. sent from the SMS dialog) instead of waiting for the next poll
defineExpose({ refresh: poll })

onMounted(async () => {
  pollTimer = setInterval(poll, 30000) // before the first await, so onUnmounted always clears it

  // Employees for message senders - the calendar page loads them, other pages may not have them yet
  if (!calendarStore.availableCalendars.length) calendarStore.updateCalendarEmployeesAndResources()

  const loaded = await fetchMessages()
  if (loaded) markAsRead()
  if (loaded === false) {
    clearInterval(pollTimer)
    emit('failed', props.client.id) // e.g. client not found
  }
  isLoading.value = false
  scrollToBottom()
  setTimeout(async () => {
    await scrollToBottom()
  }, 200)
})

onUnmounted(() => clearInterval(pollTimer))

// ========================================
// Composer
// ========================================
const normalize = ref(false) // "replace special characters" - BE strips diacritics when they are not allowed
const sms = computed(() => getSmsInfo(text.value, normalize.value))
const isOverLimit = computed(() => sms.value.parts > SMS_MAX_PARTS)
const canSend = computed(() => !!text.value.trim() && !isOverLimit.value && !isSending.value)

// The thread's width depends on the nav and the list, not just the screen - full label only when it fits with the counter centered
const footer = ref()
const { width: footerWidth } = useElementSize(footer)
const showFullLabel = computed(() => footerWidth.value >= 640)

const send = async () => {
  if (!canSend.value) return

  isSending.value = true

  const message = text.value.trim()
  const response = await customerStore.sendMessage(props.client.id, message, !normalize.value)

  if (response) {
    text.value = ''
    emit('sent', props.client.id, message)
    await fetchMessages() // BE owns the new message's status
    scrollToBottom()
  }
  isSending.value = false
}

const openCustomerCard = async () => {
  await customerStore.setSelectedCustomer({ salonerClientId: props.client.id })
  await nextTick()
  modalStore.openModal('customerCardDialog', 'conversationCustomerCardBtn')
}
</script>

<template>
  <div
    :role="embedded ? undefined : 'dialog'"
    :aria-modal="embedded ? undefined : 'true'"
    aria-labelledby="Okno rozmowy"
    class="conversation-thread d-flex flex-column bg-surface"
    :class="{ 'conversation-thread--mobile': smAndDown, 'conversation-thread--fullscreen': smAndDown && !embedded }"
  >
    <!-- 👉 Header -->
    <div
      v-if="!embedded"
      class="d-flex align-center ga-3 px-4 py-3 border-b"
    >
      <IconBtn
        v-if="smAndDown"
        class="ms-n2"
        :aria-label="$t('back')"
        @click="emit('close')"
      >
        <VIcon icon="ri-arrow-left-s-line" />
      </IconBtn>

      <VAvatar
        :color="client.color"
        variant="tonal"
        size="40"
        class="text-body-2 font-weight-medium"
      >
        <span v-if="clientName">{{ getInitials(clientName) }}</span>
        <VIcon
          v-else
          icon="ri-user-line"
        />
      </VAvatar>

      <div class="conversation-thread__title flex-grow-1">
        <div class="text-body-1 font-weight-medium text-high-emphasis text-truncate">
          {{ clientName || formatPhone(clientPhone) }}
        </div>
        <div
          v-if="clientName && clientPhone"
          class="text-caption text-medium-emphasis"
        >
          {{ formatPhone(clientPhone) }}
        </div>
      </div>

      <IconBtn
        v-if="smAndDown"
        id="conversationCustomerCardBtn"
        :aria-label="$t('customer_card')"
        @click="openCustomerCard"
      >
        <VIcon icon="ri-folder-user-line" />
      </IconBtn>
      <template v-else>
        <VBtn
          id="conversationCustomerCardBtn"
          variant="tonal"
          color="secondary"
          size="small"
          prepend-icon="ri-folder-user-line"
          @click="openCustomerCard"
        >
          {{ $t('customer_card') }}
        </VBtn>
        <IconBtn
          :aria-label="$t('close')"
          @click="emit('close')"
        >
          <VIcon icon="ri-close-line" />
        </IconBtn>
      </template>
    </div>

    <!-- 👉 Messages -->
    <div
      ref="scrollBox"
      class="conversation-thread__body flex-grow-1 overflow-y-auto bg-chat-bg d-flex flex-column ga-2 px-4 px-md-7 py-5"
    >
      <VProgressCircular
        v-if="isLoading"
        indeterminate
        color="primary"
        class="ma-auto"
      />
      <template v-else>
        <div
          v-if="meta.nextBefore"
          class="align-self-center d-flex flex-column align-center ga-1 mb-2"
        >
          <VBtn
            variant="outlined"
            color="secondary"
            rounded="pill"
            size="small"
            prepend-icon="ri-arrow-up-line"
            :loading="isLoadingOlder"
            @click="loadOlder"
          >
            {{ $t('messages_page.load_older') }}
          </VBtn>
          <span
            v-if="olderThan"
            class="text-caption text-medium-emphasis"
          >
            {{ $t('messages_page.older_than', { date: olderThan }) }}
          </span>
        </div>

        <template
          v-for="item in threadItems"
          :key="item.id"
        >
          <VChip
            v-if="item.dayLabel"
            size="small"
            class="align-self-center my-2"
          >
            {{ item.dayLabel }}
          </VChip>

          <div
            v-if="item.isNew"
            class="d-flex align-center ga-3 my-1"
          >
            <VDivider
              color="primary"
              opacity="0.5"
            />
            <span class="text-caption font-weight-medium text-primary text-no-wrap">
              {{ $t('messages_page.new_messages') }}
            </span>
            <VDivider
              color="primary"
              opacity="0.5"
            />
          </div>

          <div
            class="conversation-message d-flex flex-column ga-1"
            :class="item.isClient ? 'align-self-start align-start' : 'align-self-end align-end'"
          >
            <!-- Sender -->
            <div
              v-if="!item.isClient || item.clientName"
              class="d-flex align-center ga-1 px-1 text-caption font-weight-medium text-high-emphasis"
            >
              <template v-if="item.isClient">
                <VAvatar
                  :color="client.color"
                  variant="tonal"
                  size="18"
                  class="xxs-font-size"
                >
                  {{ getInitials(item.clientName) }}
                </VAvatar>
                {{ item.clientName }}
              </template>
              <template v-else-if="item.senderType === 'employee'">
                <EmployeeAvatar
                  v-if="item.employee"
                  :employee="item.employee"
                  size="mico"
                  :show-tooltip="false"
                />
                {{ item.employee ? item.employee.title || item.employee.fullName : $t('employee') }}
              </template>
              <template v-else>
                <SalonerLogo
                  width="14"
                  height="14"
                />
                Saloner
              </template>
            </div>

            <div
              class="conversation-bubble text-body-2"
              :class="`conversation-bubble--${item.senderType}`"
            >
              {{ item.text }}
            </div>

            <!-- Parts, time, read / delivery status -->
            <div class="d-flex align-center flex-wrap ga-1 px-1 text-caption text-medium-emphasis">
              <!-- Replaced special characters - the menu works like the "(i)" next to "Replace characters" -->
<!--              <template v-if="item.charsReplaced">-->
<!--                <VChip-->
<!--                  size="x-small"-->
<!--                  color="info"-->
<!--                  class="cursor-pointer"-->
<!--                >-->
<!--                  <small>Ą → A</small>-->
<!--                  <VMenu-->
<!--                    activator="parent"-->
<!--                    max-width="320"-->
<!--                    open-on-click-->
<!--                  >-->
<!--                    <VCard>-->
<!--                      <VCardText class="pa-2 text-caption text-high-emphasis">-->
<!--                        <div class="font-weight-medium">-->
<!--                          {{ $t('messages_page.chars_replaced') }}-->
<!--                        </div>-->
<!--                        <template v-if="item.originalText">-->
<!--                          <div class="mt-2 text-medium-emphasis">-->
<!--                            {{ $t('messages_page.original_message') }}-->
<!--                          </div>-->
<!--                          <div class="text-pre-wrap text-break">-->
<!--                            {{ item.originalText }}-->
<!--                          </div>-->
<!--                        </template>-->
<!--                      </VCardText>-->
<!--                    </VCard>-->
<!--                  </VMenu>-->
<!--                </VChip>-->
<!--                <span>·</span>-->
<!--              </template>-->
              <!--              <template v-if="item.parts">-->
              <!--                <span>{{ item.parts }} SMS</span>-->
              <!--                <span>·</span>-->
              <!--              </template>-->
              <span>{{ item.time }}</span>
              <template v-if="item.readAt">
                <span>·</span>
                <span>{{ $t('messages_page.read_at', { time: item.readAt }) }}</span>
              </template>
              <span
                v-if="item.status"
                class="d-inline-flex align-center ga-1 font-weight-medium"
                :class="item.status.color && `text-${item.status.color}`"
              >
                <VIcon
                  :icon="item.status.icon"
                  size="14"
                />
                {{ item.status.label }}
              </span>
            </div>
          </div>
        </template>

        <div
          v-if="!messages.length"
          class="ma-auto text-body-2 text-medium-emphasis"
        >
          {{ $t('noMessages') }}
        </div>
      </template>
    </div>

    <!-- 👉 Composer -->
    <div class="border-t pa-3 pa-md-4 position-relative">
<!--      <VProgressLinear-->
<!--        :active="isSending || isPolling"-->
<!--        indeterminate-->
<!--        absolute-->
<!--        location="top"-->
<!--        height="2"-->
<!--        color="primary"-->
<!--      />-->
      <CommunicationNotice
        v-if="showNotice"
        :notice="notice"
        :size="mobile ? 'small' : 'default'"
      />
      <div class="d-flex align-center ga-2">
        <SmsTextarea
          v-model="text"
          :normalize="normalize"
          :placeholder="smAndDown ? $t('messages_page.composer_placeholder_short') : $t('messages_page.composer_placeholder')"
          :rows="smAndDown ? 1 : 2"
          :auto-grow="smAndDown"
          :disabled="isSending || disabled"
          max-rows="5"
          no-resize
          hide-details
        />
        <VBtn
          v-if="sm"
          color="green"
          :disabled="!canSend"
          :loading="isSending || disabled"
          :aria-label="$t('send')"
          @click="send"
        >
          <VIcon icon="ri-send-plane-2-line" />
        </VBtn>
      </div>
      <!--
        Checkbox | counter | send button - equal side columns keep the counter centered.
        sm: the button is next to the textarea; xs: the checkbox is above the counter, left of the button.
      -->
      <div
        ref="footer"
        class="d-flex align-center flex-wrap gc-4 gr-1 mt-2 text-caption text-high-emphasis"
      >
        <SmsTextareaFooter
          v-model:normalize="normalize"
          :text="text"
          :phone="clientPhone"
          :short-label="!showFullLabel"
          :disabled="isSending || disabled"
        />
        <div
          v-if="!sm"
          class="d-flex justify-end flex-1-1-0"
        >
          <VBtn
            color="green"
            prepend-icon="ri-send-plane-2-line"
            :disabled="!canSend"
            :loading="isSending"
            @click="send"
          >
            {{ $t('send') }}
          </VBtn>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.conversation-thread {
  flex: 1 1 0;
  min-width: 0;
}

// Phones: full-screen conversation, like a chat app
.conversation-thread--fullscreen {
  position: fixed;
  z-index: 1005;
  inset: 0;
}

.conversation-thread__title {
  min-width: 0;
}

.conversation-thread__body {
  min-height: 0;

  // Overflowing flex column would squash chips/dividers to 0 height
  > * {
    flex-shrink: 0;
  }
}

.conversation-message {
  max-width: 64%;

  .conversation-thread--mobile & {
    max-width: 85%;
  }
}

.conversation-bubble {
  padding: 10px 13px;
  border-radius: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.conversation-bubble--client {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  color: rgba(var(--v-theme-on-surface), 0.72)!important;
  border-start-start-radius: 3px;
}

// Employee bubble is darker than the automatic one
.conversation-bubble--employee {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.12);
  border-start-end-radius: 3px;
  color: rgba(var(--v-theme-on-surface), 0.76)!important;
}

.conversation-bubble--system {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-start-end-radius: 3px;
  color: rgba(var(--v-theme-on-surface), 0.73)!important;
}
</style>
